import { Component, Inject, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BankStore, ContactStore } from '../../core/stores/base.store';
import { Bank } from '../../core/models';
import { LoanUtils, NotificationUtils } from '../../core/utils';
import { formatDateAsYYYYMMDD } from 'app/core/utils/date.utils';
import { NumberFormatDirective } from '../../core/utils/comma.utils';

@Component({
  selector: 'app-fund-loc-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    NumberFormatDirective
  ],
  templateUrl: './fund-loc-dialog.component.html',
  styleUrls: ['./fund-loc-dialog.component.css']
})
export class FundLocDialogComponent implements OnInit {
  paymentForm!: FormGroup;
  
  private bankStore = inject(BankStore);
  private fb = inject(FormBuilder);
  private contactStore = inject(ContactStore)
  totalAmount = 0
  selectedBank = computed(() => this.bankStore.selected());
  constructor(
    public dialogRef: MatDialogRef<FundLocDialogComponent>,
  ) {}
  
  ngOnInit(): void {
    this.paymentForm = this.fb.group({
      amount: [null, [Validators.required, Validators.min(0.01)]],
      date: [new Date(), Validators.required]
    });
    
    // Initialize the interest and total amount displays
    this.updateTotalAmount();
  }
  
  /**
   * Updates the interest and total amount based on the current amount input
   */
  updateTotalAmount(): void {
    const amount = LoanUtils.parseFormattedNumber(this.paymentForm?.get('amount')?.value);
    const interest = LoanUtils.parseFormattedNumber(this.selectedBank()?.loc_interest || 0);
    this.totalAmount = amount + interest;
  }
  
  onSubmit(): void {
    if (this.paymentForm.invalid) {
      return;
    }
    
    const formValue = this.paymentForm.value;
    const contact_id = this.contactStore.entities().find(c => c.category === 'Self')?.id;
    
    // Parse the amount and get the interest
    const amount = LoanUtils.parseFormattedNumber(formValue.amount);
    const loc_interest = LoanUtils.parseFormattedNumber(this.selectedBank()?.loc_interest || 0);
    const totalAmount = amount + loc_interest;
    const new_loc_interest = amount * LoanUtils.parseFormattedNumber(this.selectedBank()?.loc_interest_rate || 0) / 100;
    
    // Create a transaction for the total withdrawal (funding + interest)
    const fundLOClTransaction = {
      bank_id: this.selectedBank()?.id,
      transaction_date: formatDateAsYYYYMMDD(formValue.date),
      amount: -Math.abs(totalAmount),
      category: 'Fund Personal LOC',
      contact_id: contact_id,
      loan_id: null,
      insurance_id: null,
      insurance_loan_id: null,
      principal: null,
      interest: null,
      late_fee: null
    };
    
    // Create a transaction for the total withdrawal (funding + interest)
    const payLOCInterestTransaction = {
      bank_id: this.selectedBank()?.id,
      transaction_date: formatDateAsYYYYMMDD(formValue.date),
      amount: Math.abs(new_loc_interest),
      category: 'LOC Interest Credit',
      contact_id: contact_id,
      loan_id: null,
      insurance_id: null,
      insurance_loan_id: null,
      principal: null,
      interest: null,
      late_fee: null
    };
  
    
    try {
      // Save the negative transaction (withdrawal from bank)
      this.bankStore.createChild({ 
        child: fundLOClTransaction, 
        endpoint: 'bank_transaction', 
        collectionName: 'bank_transactions_collection' 
      });

      if (this.selectedBank()?.loc_interest > 0) {
        this.bankStore.createChild({ 
          child: payLOCInterestTransaction, 
          endpoint: 'bank_transaction', 
          collectionName: 'bank_transactions_collection' 
        });
      }
      
      // Update the bank's LOC balance and interest
      const bank = this.selectedBank();
      bank.loc_balance = amount;
      
      // Calculate new interest based on the funding amount
      bank.loc_interest = amount * LoanUtils.parseFormattedNumber(bank.loc_interest_rate) / 100;
      
      // Update total interest earned
      bank.loc_total_interest = LoanUtils.parseFormattedNumber(bank.loc_total_interest) + bank.loc_interest;
      
      bank.starting_balance = LoanUtils.parseFormattedNumber(bank.starting_balance);
      delete bank.loans_collection;
      delete bank.bank_transactions_collection;
      this.bankStore.update(bank);
      
      this.dialogRef.close(true);
    } catch (error) {
      NotificationUtils.getInstance().showError('Error processing LOC payment');
      console.error('Error creating LOC payment transactions:', error);
    }
  }
  
  onCancel(): void {
    this.dialogRef.close(false);
  }
}
